import { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Form, Input, notification } from "antd";
import { DownloadOutlined, InfoCircleFilled } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";

import { Loading } from "@/shared/components/Loading/Loading";
import { SEO } from "@/ui-components/commonComponents/SEO/SEO";
import { SingleFileDragger } from "@/ui-components/commonComponents/SingleFileDragger/SingleFileDragger";
import { SubmitButton } from "@/ui-components/commonComponents/Button/SubmitButton";
import { Button, ButtonColorType, ButtonSize } from "@/ui-components/commonComponents/Button/Button";
import { ValidationErrorsModal } from "@/shared/components/ValidationErrorsModal/ValidationErrorsModal";

import { ClientAbilityContext } from "@/shared/contexts/ability.context";
import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";
import { useRouteAccessController } from "@/shared/hooks/permissions/use-route-access-controller.hook";
import { useGetTestHook } from "@/shared/hooks/tests/useGetTestHook";
import { ALL_SYSTEM_LOCALES, DEFAULT_LOCALE, localeLabel, resolveEffectiveLocales } from "@/shared/utils/locale.utils";
import { getFriendlyErrorMessage } from "@/shared/services/http";
import { downloadBlob } from "@/shared/utils/download.utils";
import { LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";
import { TestTranslationHttpService } from "@/shared/services/tests/test-translation-http.service";
import { ValidationError } from "@/shared/interfaces/validation-error.interface";

import { ADMIN_ROUTE, ADD_LANGUAGE_ROUTE, TESTS_ROUTE } from "@/routing/AppRouter/routes.constants";

import LanguageChips from "@/shared/components/assessment/AboutAssessment/LanguageChips";
import LanguagesField from "@/shared/components/assessment/AboutAssessment/LanguagesField";
import style from "./style.module.css";

interface AddLanguageFormValues {
  locales: LanguagesIsoEnum[];
  file?: UploadFile;
  groupHeaderText?: Partial<Record<LanguagesIsoEnum, string>>;
}

const UNEXPECTED_UPLOAD_ERROR = "An unexpected error occurred while uploading the translations file. Please try again.";

export default function AddLanguage() {
  const navigate = useNavigate();
  const { test_id: testIdParam } = useParams();
  const testId = Number(testIdParam);

  const clientAbility = useContext(ClientAbilityContext);
  const canUpdateTest = clientAbility.can(Action.Update, Subject.TEST);
  useRouteAccessController({ slug: ADD_LANGUAGE_ROUTE, hasPermission: canUpdateTest });

  const { test, isLoading } = useGetTestHook(testId);

  const [form] = Form.useForm<AddLanguageFormValues>();
  const selectedLocales = Form.useWatch("locales", form) ?? [];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [errors, setErrors] = useState<ValidationError[] | null>(null);

  const currentLocales = resolveEffectiveLocales(test?.availableLocales);
  const newLocaleOptions = ALL_SYSTEM_LOCALES.filter(
    (locale) => locale !== DEFAULT_LOCALE && !currentLocales.includes(locale),
  );

  const englishGroupHeader = test?.config?.groupHeaderText?.[DEFAULT_LOCALE];
  const hasGroupHeader = test?.config?.groupHeaderText != null;

  const handleDownloadTemplate = async () => {
    if (selectedLocales.length === 0) {
      return;
    }
    setIsDownloading(true);
    try {
      const { blob, filename } = await TestTranslationHttpService.downloadTemplate(testId, selectedLocales);
      downloadBlob(blob, filename);
    } catch (error) {
      notification.error({
        message: getFriendlyErrorMessage(error) ?? "Could not download the template. Please try again.",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSubmit = async ({ locales, file, groupHeaderText }: AddLanguageFormValues) => {
    const uploadFile = file?.originFileObj;
    if (!uploadFile) {
      notification.error({ message: "Please upload a translation file before submitting." });
      return;
    }

    setIsSubmitting(true);
    setErrors(null);
    try {
      const result = await TestTranslationHttpService.uploadTranslation({
        testId,
        locales,
        file: uploadFile,
        groupHeaderText,
      });

      if (!result.success) {
        // Defensive: a failure with no row-level errors would otherwise close silently, since the
        // errors modal only opens when there is something to list.
        if (result.errors.length === 0) {
          notification.error({ message: UNEXPECTED_UPLOAD_ERROR });
          return;
        }
        setErrors(result.errors);
        return;
      }

      const languageNames = locales.map(localeLabel).join(", ");
      notification.success({ message: `${languageNames} added to "${test?.name}".` });
      navigate(`/${ADMIN_ROUTE}/${TESTS_ROUTE}/${testId}`);
    } catch (error) {
      notification.error({
        message: getFriendlyErrorMessage(error) ?? UNEXPECTED_UPLOAD_ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || !test) {
    return <Loading />;
  }

  return (
    <div className="main-container">
      <SEO titleParams={["Tests", test.name, "Add Language"]} />

      <div className="inner-container">
        <h1 className={style.pageTitle}>Add Language</h1>

        <div className={style.card}>
          <p className={style.fieldDescription} id="available-languages-desc">
            <strong>{test.name}</strong> is already available in:
          </p>
          <div className={style.availableList}>
            <LanguageChips value={currentLocales} aria-describedby="available-languages-desc" />
          </div>

          {newLocaleOptions.length === 0 ? (
            <Alert
              className={style.allLanguagesAlert}
              type="info"
              showIcon={true}
              icon={<InfoCircleFilled className={style.infoNoteIcon} />}
              message="This test is already available in every supported language."
            />
          ) : (
            <Form
              form={form}
              className={style.form}
              layout="vertical"
              requiredMark={false}
              initialValues={{ locales: [] }}
              onFinish={handleSubmit}
              onFinishFailed={() => {
                notification.error({ message: "Please fix the highlighted errors before submitting." });
              }}
            >
              {/* A ul isn't labelable, so the group is named via aria-labelledby rather than htmlFor. */}
              <Form.Item label={<span id="new-languages-label">New Languages</span>}>
                <p className={style.fieldDescription} id="locales-desc">
                  Select the language(s) you want to add to <strong>{test.name}</strong>.
                </p>
                <Form.Item
                  name="locales"
                  noStyle={true}
                  rules={[
                    { required: true, type: "array", min: 1, message: "Select at least one language to upload." },
                  ]}
                >
                  <LanguagesField
                    locales={newLocaleOptions}
                    aria-labelledby="new-languages-label"
                    aria-describedby="locales-desc"
                  />
                </Form.Item>
              </Form.Item>

              {hasGroupHeader && selectedLocales.length > 0 && (
                <>
                  <p className={style.fieldDescription} id="group-header-desc">
                    This test displays a header above its question groups. In English it reads{" "}
                    <strong>&ldquo;{englishGroupHeader}&rdquo;</strong> — enter the translated header for each new
                    language.
                  </p>
                  {selectedLocales.map((locale) => (
                    <Form.Item
                      key={locale}
                      name={["groupHeaderText", locale]}
                      label={`Group Header (${localeLabel(locale)})`}
                      preserve={false}
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: `Enter the ${localeLabel(locale)} translation of the group header.`,
                        },
                      ]}
                    >
                      <Input aria-describedby="group-header-desc" />
                    </Form.Item>
                  ))}
                </>
              )}

              <Form.Item label="Translation File">
                <div className={style.downloadRow}>
                  <Button
                    colorType={ButtonColorType.light}
                    buttonSize={ButtonSize.small}
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadTemplate}
                    disabled={selectedLocales.length === 0}
                    isLoading={isDownloading}
                  >
                    Download template
                  </Button>
                  <span className={style.hint}>
                    {selectedLocales.length > 0
                      ? "Fill in each language's column with the translation, then upload the file below."
                      : "Select a language to download its translation template."}
                  </span>
                </div>
                <Form.Item
                  name="file"
                  noStyle={true}
                  valuePropName="file"
                  rules={[{ required: true, message: "Please upload a ZIP or CSV file." }]}
                >
                  <SingleFileDragger accept=".zip,.csv" hintText="Support for a single ZIP or CSV file upload" />
                </Form.Item>
              </Form.Item>

              <div className={style.submitRow}>
                <SubmitButton arrowAfter={true} isLoading={isSubmitting}>
                  Submit
                </SubmitButton>
              </div>
            </Form>
          )}
        </div>
      </div>

      <ValidationErrorsModal title="Translation File Upload Failed" errors={errors} onClose={() => setErrors(null)} />
    </div>
  );
}
